import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { X, Plus, Loader2, ChevronUp, ChevronDown, ChevronLeft, Trash2, Search, BookOpen, Bookmark, GripVertical, Youtube, AlertTriangle } from 'lucide-react';
import { Collection, CollectionItem, Language } from '../types';
import {
    subscribeCollections, createCollection, renameCollection, updateCollectionDescription,
    deleteCollection, removeItemFromCollection, reorderCollectionItems, addItemToCollection,
} from '../services/collectionsService';
import { searchAcrossNotebooks, CrossNotebookSearchResult, getCollectionItemsInfo, CollectionItemInfo } from '../services/geminiService';

interface Props {
    language: Language;
    onClose: () => void;
    onNavigateToNote?: (notebookId: string, nodeId: string) => void;
    initialSelectedId?: string | null;
}

// Stable per-notebook badge color, so at a glance you can see which source each note came from.
const BADGE_COLORS = [
    'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-900/40',
    'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-900/40',
    'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-900/40',
    'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-900/40',
    'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-300 dark:border-violet-900/40',
    'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/20 dark:text-cyan-300 dark:border-cyan-900/40',
    'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-900/40',
    'bg-lime-50 text-lime-700 border-lime-200 dark:bg-lime-900/20 dark:text-lime-300 dark:border-lime-900/40',
];
const badgeColor = (notebookId: string) => {
    let h = 0;
    for (let i = 0; i < notebookId.length; i++) h = (h * 31 + notebookId.charCodeAt(i)) >>> 0;
    return BADGE_COLORS[h % BADGE_COLORS.length];
};
const itemKey = (i: { notebookId: string; nodeId: string }) => `${i.notebookId}/${i.nodeId}`;

export function CollectionsHubModal({ language, onClose, onNavigateToNote, initialSelectedId }: Props) {
    const vn = language === 'vietnamese';
    const uid = getAuth().currentUser?.uid;

    const [collections, setCollections] = useState<Collection[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId ?? null);
    const [newTitle, setNewTitle] = useState('');
    const [titleDraft, setTitleDraft] = useState('');
    const [descDraft, setDescDraft] = useState('');

    const [itemInfo, setItemInfo] = useState<Record<string, CollectionItemInfo>>({});
    const [dragIdx, setDragIdx] = useState<number | null>(null);
    const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

    const [showAddNote, setShowAddNote] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<CrossNotebookSearchResult[]>([]);
    const [searching, setSearching] = useState(false);
    const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!uid) { setLoading(false); return; }
        return subscribeCollections(uid, cols => {
            setCollections([...cols].sort((a, b) => b.updatedAt - a.updatedAt));
            setLoading(false);
        });
    }, [uid]);

    const selected = collections.find(c => c.id === selectedId) || null;
    const sortedItems = useMemo(() => selected ? [...selected.items].sort((a, b) => a.order - b.order) : [], [selected]);

    useEffect(() => {
        setTitleDraft(selected?.title || '');
        setDescDraft(selected?.description || '');
    }, [selectedId, selected?.title, selected?.description]);

    // Live info only needs refetching when the SET of referenced notes changes, not on reorder.
    const itemSetKey = useMemo(() => sortedItems.map(itemKey).sort().join('|'), [sortedItems]);
    useEffect(() => {
        if (!itemSetKey) { setItemInfo({}); return; }
        let cancelled = false;
        getCollectionItemsInfo(sortedItems).then(info => { if (!cancelled) setItemInfo(info); }).catch(() => {});
        return () => { cancelled = true; };
    }, [itemSetKey]);

    useEffect(() => {
        if (!searchQuery.trim()) { setSearchResults([]); setSearching(false); return; }
        if (searchDebounce.current) clearTimeout(searchDebounce.current);
        setSearching(true);
        searchDebounce.current = setTimeout(() => {
            searchAcrossNotebooks(searchQuery.trim()).then(setSearchResults).catch(() => setSearchResults([])).finally(() => setSearching(false));
        }, 250);
        return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
    }, [searchQuery]);

    if (!uid) return null;

    const handleCreate = async () => {
        const title = newTitle.trim();
        if (!title) return;
        const id = await createCollection(uid, title);
        setNewTitle('');
        setSelectedId(id);
    };

    const commitTitle = () => {
        if (!selected) return;
        if (titleDraft.trim() === selected.title) return;
        renameCollection(uid, selected.id, titleDraft);
    };

    const commitOrder = (next: CollectionItem[]) => {
        if (selected) reorderCollectionItems(uid, selected.id, next);
    };
    const moveItem = (idx: number, dir: -1 | 1) => {
        const j = idx + dir;
        if (j < 0 || j >= sortedItems.length) return;
        const next = [...sortedItems];
        [next[idx], next[j]] = [next[j], next[idx]];
        commitOrder(next);
    };
    const dropAt = (to: number) => {
        if (dragIdx === null || dragIdx === to) return;
        const next = [...sortedItems];
        const [moved] = next.splice(dragIdx, 1);
        next.splice(to, 0, moved);
        commitOrder(next);
    };

    return (
        <div className="fixed inset-0 z-[100000] bg-black/50 flex items-center justify-center p-0 md:p-4">
            <div className="bg-white dark:bg-slate-900 md:rounded-2xl shadow-2xl w-full max-w-4xl h-full md:h-[85vh] flex overflow-hidden">
                {/* Left: courses list — on mobile, hidden once a course is open */}
                <div className={`${selected ? 'hidden md:flex' : 'flex'} w-full md:w-72 shrink-0 md:border-r border-gray-200 dark:border-slate-700 flex-col`}>
                    <div className="p-4 border-b border-gray-100 dark:border-slate-800 flex items-center gap-2">
                        <Bookmark size={18} className="text-indigo-500" />
                        <h3 className="flex-1 font-bold text-gray-800 dark:text-gray-100">{vn ? 'Bộ sưu tập' : 'Collections'}</h3>
                        <button onClick={onClose} className="md:hidden text-gray-400 hover:text-gray-600"><X size={18} /></button>
                    </div>
                    <div className="p-3 flex gap-1.5 border-b border-gray-100 dark:border-slate-800">
                        <input
                            value={newTitle}
                            onChange={e => setNewTitle(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleCreate()}
                            placeholder={vn ? 'Tên bộ sưu tập mới...' : 'New collection name...'}
                            className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 text-sm text-gray-800 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-300"
                        />
                        <button onClick={handleCreate} disabled={!newTitle.trim()} className="p-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 text-white shrink-0">
                            <Plus size={16} />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-1">
                        {loading && <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin text-gray-300" /></div>}
                        {!loading && collections.length === 0 && (
                            <p className="text-xs text-gray-400 text-center py-8 px-4">{vn ? 'Chưa có bộ sưu tập nào. Tạo cái đầu tiên ở trên, hoặc bấm nút bookmark trong 1 note.' : 'No collections yet. Create one above, or hit the bookmark button in any note.'}</p>
                        )}
                        {collections.map(c => (
                            <button
                                key={c.id}
                                onClick={() => setSelectedId(c.id)}
                                className={`w-full text-left px-3 py-2.5 rounded-xl transition-colors ${selectedId === c.id ? 'bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-200 dark:ring-indigo-800' : 'hover:bg-gray-100 dark:hover:bg-slate-800'}`}
                            >
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{c.title}</p>
                                <p className="text-[11px] text-gray-400">{c.items.length} {vn ? 'note' : 'notes'}</p>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Right: course detail — on mobile, only shown once a course is open */}
                <div className={`${selected ? 'flex' : 'hidden md:flex'} flex-1 flex-col min-w-0`}>
                    <div className="p-4 border-b border-gray-100 dark:border-slate-800 flex items-center gap-2">
                        <button onClick={() => setSelectedId(null)} className="md:hidden text-gray-500"><ChevronLeft size={18} /></button>
                        <h3 className="flex-1 font-bold text-gray-800 dark:text-gray-100">{vn ? 'Chi tiết' : 'Details'}</h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><X size={18} /></button>
                    </div>

                    {!selected && (
                        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                            {vn ? 'Chọn 1 bộ sưu tập bên trái để xem chi tiết' : 'Select a collection on the left to see details'}
                        </div>
                    )}

                    {selected && (
                        <div className="flex-1 overflow-y-auto p-4 md:p-5 flex flex-col gap-4">
                            <div className="flex items-center gap-2">
                                <input
                                    value={titleDraft}
                                    onChange={e => setTitleDraft(e.target.value)}
                                    onBlur={commitTitle}
                                    onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                    className="flex-1 min-w-0 text-lg font-bold text-gray-800 dark:text-gray-100 bg-transparent outline-none border-b border-transparent focus:border-indigo-300 pb-0.5"
                                />
                                <button
                                    onClick={() => { if (confirm(vn ? `Xóa bộ sưu tập "${selected.title}"?` : `Delete collection "${selected.title}"?`)) { deleteCollection(uid, selected.id); setSelectedId(null); } }}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 shrink-0"
                                >
                                    <Trash2 size={15} />
                                </button>
                            </div>

                            <textarea
                                value={descDraft}
                                onChange={e => setDescDraft(e.target.value)}
                                onBlur={() => { if (descDraft !== (selected.description || '')) updateCollectionDescription(uid, selected.id, descDraft); }}
                                placeholder={vn ? 'Mô tả ngắn (tùy chọn)...' : 'Short description (optional)...'}
                                rows={2}
                                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 text-sm text-gray-700 dark:text-gray-200 outline-none focus:ring-2 focus:ring-indigo-300 resize-none"
                            />

                            <div className="flex items-center justify-between">
                                <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                                    {sortedItems.length} {vn ? 'note' : 'notes'}
                                </h4>
                                <button
                                    onClick={() => setShowAddNote(true)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-lg text-xs font-bold hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
                                >
                                    <Plus size={12} /> {vn ? 'Thêm note' : 'Add note'}
                                </button>
                            </div>

                            <div className="flex flex-col gap-1.5">
                                {sortedItems.length === 0 && (
                                    <p className="text-xs text-gray-400 text-center py-6">{vn ? 'Chưa có note nào' : 'No notes yet'}</p>
                                )}
                                {sortedItems.map((item, idx) => {
                                    const info = itemInfo[itemKey(item)];
                                    const deleted = info?.exists === false;
                                    const title = info?.title || item.nodeTitle || (vn ? 'Không có tiêu đề' : 'Untitled');
                                    return (
                                        <div
                                            key={itemKey(item)}
                                            draggable
                                            onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDragIdx(idx); }}
                                            onDragOver={e => { e.preventDefault(); setDragOverIdx(idx); }}
                                            onDrop={e => { e.preventDefault(); dropAt(idx); setDragIdx(null); setDragOverIdx(null); }}
                                            onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                                            className={`flex items-center gap-2 border rounded-lg px-2 py-2 transition-colors ${
                                                dragIdx === idx ? 'opacity-40' :
                                                dragOverIdx === idx && dragIdx !== null ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20' :
                                                deleted ? 'border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-900/10' :
                                                'border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800'
                                            }`}
                                        >
                                            <GripVertical size={14} className="text-gray-300 dark:text-slate-600 cursor-grab shrink-0" />
                                            <span className="text-xs font-bold text-gray-400 w-5 shrink-0 tabular-nums">{idx + 1}.</span>
                                            <button
                                                disabled={deleted}
                                                onClick={() => onNavigateToNote?.(item.notebookId, item.nodeId)}
                                                className="flex-1 min-w-0 text-left disabled:cursor-default"
                                            >
                                                {deleted ? (
                                                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1 truncate">
                                                        <AlertTriangle size={12} className="shrink-0" />
                                                        {vn ? 'Note đã bị xóa' : 'Note was deleted'} <span className="text-gray-400 line-through truncate">— {item.nodeTitle}</span>
                                                    </p>
                                                ) : (
                                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{title}</p>
                                                )}
                                                <span className={`inline-block mt-0.5 px-1.5 py-px rounded border text-[10px] font-medium max-w-full truncate ${badgeColor(item.notebookId)}`}>
                                                    {item.notebookTitle || '?'}
                                                </span>
                                            </button>
                                            {info?.hasVideo && <Youtube size={14} className="text-red-500 shrink-0" />}
                                            {typeof info?.score === 'number' && (
                                                <span className="text-[11px] font-bold tabular-nums text-gray-500 dark:text-gray-400 shrink-0">{info.score}</span>
                                            )}
                                            <div className="flex flex-col shrink-0">
                                                <button disabled={idx === 0} onClick={() => moveItem(idx, -1)} className="p-0.5 text-gray-400 hover:text-indigo-500 disabled:opacity-20"><ChevronUp size={13} /></button>
                                                <button disabled={idx === sortedItems.length - 1} onClick={() => moveItem(idx, 1)} className="p-0.5 text-gray-400 hover:text-indigo-500 disabled:opacity-20"><ChevronDown size={13} /></button>
                                            </div>
                                            <button
                                                onClick={() => removeItemFromCollection(uid, selected.id, selected.items, item.nodeId, item.notebookId)}
                                                className="p-1 text-gray-400 hover:text-red-600 shrink-0"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add-note search overlay */}
            {showAddNote && selected && (
                <div className="fixed inset-0 z-[100001] bg-black/40 flex items-center justify-center p-4" onClick={() => { setShowAddNote(false); setSearchQuery(''); }}>
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 p-4 w-full max-w-md max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-bold text-gray-800 dark:text-gray-100">{vn ? 'Thêm note' : 'Add note'}</h4>
                            <button onClick={() => { setShowAddNote(false); setSearchQuery(''); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><X size={16} /></button>
                        </div>
                        <div className="relative mb-3">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                autoFocus
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                placeholder={vn ? 'Tìm note theo tên/nội dung...' : 'Search notes by title/content...'}
                                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900 text-sm text-gray-800 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-300"
                            />
                        </div>
                        <div className="flex-1 overflow-y-auto flex flex-col gap-1.5">
                            {searching && <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin text-gray-300" /></div>}
                            {!searching && searchQuery.trim() && searchResults.length === 0 && (
                                <p className="text-xs text-gray-400 text-center py-8">{vn ? 'Không tìm thấy note nào' : 'No notes found'}</p>
                            )}
                            {!searching && searchResults.map(r => {
                                const already = selected.items.some(i => i.nodeId === r.nodeId && i.notebookId === r.notebookId);
                                return (
                                    <button
                                        key={`${r.notebookId}-${r.nodeId}`}
                                        disabled={already}
                                        onClick={async () => {
                                            const ok = await addItemToCollection(uid, selected.id, selected.items, {
                                                nodeId: r.nodeId, notebookId: r.notebookId, nodeTitle: r.nodeTitle || '', notebookTitle: r.notebookTitle,
                                            });
                                            if (ok) { setShowAddNote(false); setSearchQuery(''); }
                                        }}
                                        className="flex items-center gap-2 text-left px-2.5 py-2 rounded-lg border border-gray-200 dark:border-slate-600 hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
                                    >
                                        <BookOpen size={14} className="text-gray-400 shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{r.nodeTitle || (vn ? 'Không có tiêu đề' : 'Untitled')}</p>
                                            <span className={`inline-block mt-0.5 px-1.5 py-px rounded border text-[10px] font-medium ${badgeColor(r.notebookId)}`}>{r.notebookTitle}</span>
                                        </div>
                                        {already && <span className="text-[10px] text-gray-400 shrink-0">{vn ? 'đã có' : 'added'}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
